import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCity } from '../contexts/CityContext';
import { Logo } from '../components/Logo';
import { GoogleLoginButton } from '../components/GoogleLoginButton';
import { BottomNav } from '../components/BottomNav';
import { Loading } from '../components/Loading';
import api from '../services/api';

export function ProfilePage() {
  const { user, isVisitor, isAdmin, login, loginGoogle, logout, error: authError } = useAuth();
  const { selectedCity } = useCity();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [pendingPixCount, setPendingPixCount] = useState(0);
  const [creditHistory, setCreditHistory] = useState([]);
  const [creditHistoryLoading, setCreditHistoryLoading] = useState(false);

  // Busca histórico de créditos
  useEffect(() => {
    if (isVisitor) return;
    (async () => {
      setCreditHistoryLoading(true);
      try {
        const { data } = await api.get('/payments/credits/history');
        setCreditHistory((data.data || []).slice(0, 15));
      } catch (_) {
        setCreditHistory([]);
      } finally {
        setCreditHistoryLoading(false);
      }
    })();
  }, [isVisitor]);

  // Fetch pending Pix count for admin badge
  useEffect(() => {
    if (!isAdmin || isVisitor) return;
    (async () => {
      try {
        const { data } = await api.get('/payments/admin/payments/pending');
        setPendingPixCount((data.data || []).length);
      } catch (_) { setPendingPixCount(0); }
    })();
    const poll = setInterval(async () => {
      try {
        const { data } = await api.get('/payments/admin/payments/pending');
        setPendingPixCount((data.data || []).length);
      } catch (_) {}
    }, 30000);
    return () => clearInterval(poll);
  }, [isAdmin, isVisitor]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);
    try {
      await login(email, password);
      setShowLoginForm(false);
    } catch (err) {
      setLoginError(err.response?.data?.error?.message || 'Erro ao fazer login');
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div className="nc-page nc-page--nav">
      <div style={{
        background: 'var(--nc-primary)', color: 'var(--nc-on-primary)', padding: '24px 16px 32px',
        borderRadius: '0 0 24px 24px'
      }}>
        <div style={{ marginBottom: 20 }}>
          <Logo size={36} showText={false} />
        </div>
        {isVisitor ? (
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 6 }}>Bem-vindo!</h1>
            <p style={{ fontSize: 14, opacity: 0.8, marginBottom: 16 }}>
              Faça login para favoritar, avaliar e publicar.
            </p>

            {!showLoginForm ? (
              <>
                <button
                  onClick={() => setShowLoginForm(true)}
                  style={{
                    background: 'var(--nc-surface)', color: 'var(--nc-primary)', border: 'none', borderRadius: 10,
                    padding: '12px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%', justifyContent: 'center',
                    marginBottom: 8
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" />
                  </svg>
                  Entrar com Email
                </button>
                {/* LE-034: login com Google — só aparece quando o build tem
                    REACT_APP_GOOGLE_CLIENT_ID (desabilitado → não renderiza) */}
                <GoogleLoginButton
                  onSuccess={async (credential) => {
                    await loginGoogle(credential);
                    setShowLoginForm(false);
                  }}
                  onError={(err) => setLoginError(err.response?.data?.error?.message || err.message || 'Erro ao entrar com Google')}
                />
              </>
            ) : (
              <form onSubmit={handleLogin} style={{ maxWidth: 280 }}>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.1)', color: 'var(--nc-on-primary)', fontSize: 14, marginBottom: 8,
                    outline: 'none', boxSizing: 'border-box'
                  }}
                  className="nc-input-dark"
                />
                <input
                  type="password"
                  placeholder="Senha"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.1)', color: 'var(--nc-on-primary)', fontSize: 14, marginBottom: 8,
                    outline: 'none', boxSizing: 'border-box'
                  }}
                  className="nc-input-dark"
                />
                {loginError && (
                  <div style={{ fontSize: 12, color: '#fca5a5', marginBottom: 8 }}>{loginError}</div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="submit"
                    disabled={loginLoading}
                    style={{
                      flex: 1, background: 'var(--nc-surface)', color: 'var(--nc-primary)', border: 'none', borderRadius: 8,
                      padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                      opacity: loginLoading ? 0.7 : 1
                    }}
                  >
                    {loginLoading ? 'Entrando...' : 'Entrar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowLoginForm(false)}
                    style={{
                      background: 'rgba(255,255,255,0.1)', color: 'var(--nc-on-primary)', border: 'none', borderRadius: 8,
                      padding: '10px 16px', fontSize: 14, cursor: 'pointer'
                    }}
                  >
                    Voltar
                  </button>
                </div>
              </form>
            )}
            {authError && (
              <div style={{ fontSize: 12, color: '#fca5a5', marginTop: 8 }}>{authError}</div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24
            }}>
              {user.avatar ? (
                <img src={user.avatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : '👤'}
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{user.name}</div>
              <div style={{ fontSize: 13, opacity: 0.8 }}>{user.email}</div>
            </div>
          </div>
        )}
      </div>

      <div className="nc-container" style={{ paddingTop: 16, paddingBottom: 16 }}>
        {/* City Info */}
        <div style={{
          background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)',
          padding: 14, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, background: 'var(--nc-success-soft)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--nc-primary)'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: 'var(--nc-text-muted)' }}>Cidade atual</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)' }}>
              {selectedCity?.name} / {selectedCity?.state}
            </div>
          </div>
          <button
            onClick={() => navigate('/cidade')}
            style={{ background: 'none', border: 'none', color: 'var(--nc-primary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
          >
            Trocar
          </button>
        </div>

        {/* Menu Items */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[
            ...(isVisitor
              ? [{ icon: '🔑', label: 'Entrar / Criar conta', action: () => navigate('/entrar') }]
              : [
                  { icon: '⚙️', label: 'Minha conta', action: () => navigate('/minha-conta') },
                  { icon: '🏪', label: 'Meus estabelecimentos', action: () => navigate('/comerciante/estabelecimentos') },
                  { icon: '➕', label: 'Cadastrar comércio', action: () => navigate('/comerciante/solicitar') },
                  { icon: '🛵', label: 'Perfil de entregador', action: () => navigate('/entregador/perfil') },
                  { icon: '📦', label: 'Meus pedidos de entrega', action: () => navigate('/entregador/pedidos') },
                ]
            ),
            ...(isAdmin
              ? [{ icon: '🛡️', label: 'Painel OthoSystems (admin)', action: () => navigate('/admin'), badge: pendingPixCount }]
              : []
            ),
            { icon: '💎', label: 'Comprar créditos', action: () => navigate('/comprar-creditos') },
            { icon: '⭐', label: 'Meus favoritos', action: () => navigate('/favoritos') },
            { icon: '📋', label: 'Meus pedidos', action: () => navigate('/pedidos') },
            { icon: '🔔', label: 'Notificações', action: () => navigate('/notificacoes') },
            { icon: '❓', label: 'Ajuda', action: () => navigate('/ajuda') },
          ].map((item, i) => (
            <button
              key={i}
              onClick={item.action}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                background: 'var(--nc-surface)', border: '1px solid var(--nc-border)', borderRadius: 10,
                textAlign: 'left', cursor: 'pointer', fontSize: 14, color: 'var(--nc-text)', fontWeight: 500
              }}
            >
              <span style={{ fontSize: 18 }}>{item.icon}</span>
              <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                {item.label}
                {item.badge > 0 && (
                  <span style={{
                    background: 'var(--nc-danger)', color: 'var(--nc-on-primary)', fontSize: 11, fontWeight: 700,
                    minWidth: 18, height: 18, borderRadius: 9, display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center', padding: '0 4px',
                    animation: 'nc-pulse-badge 2s infinite',
                  }}>
                    {item.badge}
                  </span>
                )}
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          ))}
        </div>

        {/* ───────── Histórico de créditos ───────── */}
        {!isVisitor && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nc-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '18px 0 8px' }}>
              💳 Histórico de créditos
            </div>
            {creditHistoryLoading ? <Loading /> : creditHistory.length === 0 ? (
              <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 14, textAlign: 'center', fontSize: 13, color: 'var(--nc-text-muted)' }}>
                Nenhum movimento de créditos ainda.
              </div>
            ) : (
              <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', overflow: 'hidden' }}>
                {creditHistory.map(t => {
                  const isPositive = t.type === 'purchase' || t.type === 'bonus';
                  const emoji = t.type === 'purchase' ? '💵' : t.type === 'spend' ? '⚡' : t.type === 'bonus' ? '🎁' : '🔄';
                  const dt = new Date(t.created_at);
                  const timeStr = dt.toLocaleDateString('pt-BR') + ' ' + dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                  return (
                    <div key={t.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
                      borderBottom: '1px solid var(--nc-border)',
                    }}>
                      <span style={{ fontSize: 18, flexShrink: 0 }}>{emoji}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nc-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.label}
                        </div>
                        <div className="nc-caption">{timeStr}</div>
                      </div>
                      <div style={{
                        fontSize: 14, fontWeight: 700,
                        color: isPositive ? '#15803d' : '#b45309',
                        flexShrink: 0,
                      }}>
                        {isPositive ? '+' : ''}{t.amount}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {!isVisitor && (
          <button
            onClick={logout}
            style={{

              width: '100%', marginTop: 16, padding: 14, borderRadius: 10,
              background: 'var(--nc-danger-soft)', color: 'var(--nc-danger)', border: '1px solid var(--nc-danger-border)',
              fontSize: 14, fontWeight: 600, cursor: 'pointer'
            }}
          >
            Sair da conta
          </button>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
